import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  fetchWbsTargets,
  reCalculateWbsTargets,
  upsertWbsTargets,
  type WbsTargetFrequency,
  type WbsTargetRow,
} from '../../../../api/wbsTargetApis';
import type { TaskSnapshot } from '../../../../stores/types';
import type { TargetsPeriodColumn } from './targetsPeriodUtils';

export type PeriodQuantity = { target: number; achieved: number };

/** unitActivityId → periodKey → quantities */
export type TargetsQuantityMap = Map<string, Map<string, PeriodQuantity>>;

function periodKey(frequency: WbsTargetFrequency, targetPeriod: string): string {
  return `${frequency}:${targetPeriod}`;
}

function buildQuantityMap(
  rows: WbsTargetRow[],
  columns: TargetsPeriodColumn[],
): TargetsQuantityMap {
  const allowed = new Set(columns.map((c) => periodKey(c.frequency, c.targetPeriod)));
  const map: TargetsQuantityMap = new Map();
  for (const row of rows) {
    const freq = row.frequency.toUpperCase() as WbsTargetFrequency;
    const key = periodKey(freq, row.targetPeriod);
    if (!allowed.has(key) && columns.length > 0) {
      // Still accept exact targetPeriod matches for the active frequency
      const colMatch = columns.some(
        (c) => c.frequency === freq && c.targetPeriod === row.targetPeriod,
      );
      if (!colMatch) continue;
    }
    let byPeriod = map.get(row.unitActivityId);
    if (!byPeriod) {
      byPeriod = new Map();
      map.set(row.unitActivityId, byPeriod);
    }
    byPeriod.set(key, { target: row.quantity, achieved: row.achievedQuantity });
  }
  return map;
}

export function getPrimaryUnitActivityId(task: TaskSnapshot | null | undefined): string | null {
  const details = task?.unitActivityDetails;
  if (!Array.isArray(details) || details.length === 0) return null;
  const id = String(details[0]?.id ?? '').trim();
  return id || null;
}

export function useTaskTargetsData(opts: {
  projectId: string;
  enabled: boolean;
  columns: TargetsPeriodColumn[];
  frequency: WbsTargetFrequency;
  /** Tasks currently shown in the targets/Gantt tree (look-ahead filtered). */
  tasks: TaskSnapshot[];
}) {
  const { projectId, enabled, columns, frequency, tasks } = opts;
  const [loading, setLoading] = useState(false);
  const [baseMap, setBaseMap] = useState<TargetsQuantityMap>(() => new Map());
  /** `${unitActivityId}::${periodKey}` → quantity */
  const [drafts, setDrafts] = useState<Record<string, number>>({});
  const [saving, setSaving] = useState(false);
  const [recalculating, setRecalculating] = useState(false);
  const [reloadToken, setReloadToken] = useState(0);

  const unitActivityIds = useMemo(() => {
    const ids = new Set<string>();
    for (const t of tasks) {
      const ua = getPrimaryUnitActivityId(t);
      if (ua) ids.add(ua);
    }
    return [...ids];
  }, [tasks]);

  const columnsKey = useMemo(
    () => columns.map((c) => `${c.frequency}:${c.targetPeriod}`).join('|'),
    [columns],
  );

  const refresh = useCallback(() => {
    setReloadToken((n) => n + 1);
  }, []);

  useEffect(() => {
    if (!enabled || !projectId || unitActivityIds.length === 0 || columns.length === 0) {
      setBaseMap(new Map());
      setDrafts({});
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    void fetchWbsTargets({
      projectId,
      unitActivityIds,
      frequency: [frequency],
    })
      .then((rows) => {
        if (cancelled) return;
        setBaseMap(buildQuantityMap(rows, columns));
        setDrafts({});
      })
      .catch(() => {
        if (cancelled) return;
        setBaseMap(new Map());
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // columnsKey stands in for columns content
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, projectId, unitActivityIds.join(','), frequency, columnsKey, reloadToken]);

  const dirtyCount = useMemo(() => Object.keys(drafts).length, [drafts]);

  const getCellValue = useCallback(
    (unitActivityId: string | null, column: TargetsPeriodColumn): PeriodQuantity => {
      if (!unitActivityId) return { target: 0, achieved: 0 };
      const key = periodKey(column.frequency, column.targetPeriod);
      const draftKey = `${unitActivityId}::${key}`;
      const base = baseMap.get(unitActivityId)?.get(key) ?? { target: 0, achieved: 0 };
      if (Object.prototype.hasOwnProperty.call(drafts, draftKey)) {
        return { target: drafts[draftKey]!, achieved: base.achieved };
      }
      return base;
    },
    [baseMap, drafts],
  );

  const applyDraftValue = (
    next: Record<string, number>,
    unitActivityId: string,
    column: TargetsPeriodColumn,
    value: number | null,
  ) => {
    const key = periodKey(column.frequency, column.targetPeriod);
    const draftKey = `${unitActivityId}::${key}`;
    const base = baseMap.get(unitActivityId)?.get(key)?.target ?? 0;
    // null = discard draft for this cell (revert to saved). Use 0 explicitly to clear.
    if (value == null || Number.isNaN(value) || value === base) {
      delete next[draftKey];
    } else {
      next[draftKey] = value;
    }
  };

  const setCellTarget = useCallback(
    (unitActivityId: string, column: TargetsPeriodColumn, value: number | null) => {
      setDrafts((prev) => {
        const next = { ...prev };
        applyDraftValue(next, unitActivityId, column, value);
        return next;
      });
    },
    // applyDraftValue closes over baseMap
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [baseMap],
  );

  /** Apply many draft cell updates in one setState (paste). */
  const setCellTargets = useCallback(
    (
      updates: Array<{
        unitActivityId: string;
        column: TargetsPeriodColumn;
        value: number | null;
      }>,
    ) => {
      if (updates.length === 0) return;
      setDrafts((prev) => {
        const next = { ...prev };
        for (const u of updates) {
          applyDraftValue(next, u.unitActivityId, u.column, u.value);
        }
        return next;
      });
    },
    // applyDraftValue closes over baseMap
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [baseMap],
  );

  const isCellDirty = useCallback(
    (unitActivityId: string | null, column: TargetsPeriodColumn): boolean => {
      if (!unitActivityId) return false;
      const key = periodKey(column.frequency, column.targetPeriod);
      return Object.prototype.hasOwnProperty.call(drafts, `${unitActivityId}::${key}`);
    },
    [drafts],
  );

  const save = useCallback(async () => {
    const scheduleList = Object.entries(drafts).map(([draftKey, quantity]) => {
      const [unitActivityId, key] = draftKey.split('::');
      const [freq, ...periodParts] = (key ?? '').split(':');
      return {
        unitActivityId: unitActivityId!,
        frequency: (freq as WbsTargetFrequency) || frequency,
        targetPeriod: periodParts.join(':'),
        quantity,
      };
    });
    if (scheduleList.length === 0) return;
    setSaving(true);
    try {
      await upsertWbsTargets({ projectId, scheduleList });
      setDrafts({});
      setReloadToken((n) => n + 1);
    } finally {
      setSaving(false);
    }
  }, [drafts, frequency, projectId]);

  const discardDrafts = useCallback(() => {
    setDrafts({});
  }, []);

  /**
   * Portal-equivalent auto-generate for the current calendar month (YYYY-MM),
   * then reload the grid. Intended for Day scale only at the call site.
   */
  const recalculate = useCallback(async () => {
    if (!projectId || recalculating) return;
    const now = new Date();
    const targetPeriod = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    setRecalculating(true);
    try {
      await reCalculateWbsTargets({ projectId, targetPeriod });
      setDrafts({});
      setReloadToken((n) => n + 1);
    } finally {
      setRecalculating(false);
    }
  }, [projectId, recalculating]);

  return {
    loading,
    saving,
    recalculating,
    dirtyCount,
    getCellValue,
    setCellTarget,
    setCellTargets,
    isCellDirty,
    save,
    refresh,
    recalculate,
    discardDrafts,
  };
}
